import { useState, useEffect } from "react";
import { ZoomIn, Info, X } from "lucide-react";

// Image descriptions for accessibility
export const IMAGE_DESCRIPTIONS = {
  hero: "A stunning aerial view of a luxury tropical villa with a private infinity pool overlooking lush palm trees and the Caribbean Sea. The villa features modern architecture with open-air living spaces designed to embrace the tropical climate.",
  buggy: "An exciting off-road buggy adventure through the Dominican Republic jungle. Participants ride in rugged all-terrain vehicles along dirt trails surrounded by tropical vegetation, experiencing the thrill of exploring nature.",
  beach: "The pristine turquoise waters of Juan Dolio beach with swaying palm trees along the shoreline. Golden sand meets crystal-clear Caribbean waters under a brilliant blue sky, showcasing the natural beauty of the Dominican Republic coastline.",
  villa: "A beautifully appointed vacation villa featuring elegant tropical décor, comfortable furnishings, and modern amenities. Large windows and outdoor spaces allow guests to enjoy the warm Caribbean breeze and stunning views.",
  logo: "Palmaréca Selah Village - A luxurious tropical retreat nestled in the heart of Juan Dolio, Dominican Republic. Our logo represents the perfect blend of Caribbean elegance and natural beauty, featuring palm trees that symbolize our commitment to providing an authentic tropical paradise experience."
};

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_tropical-escape-7/artifacts/3s0oye0z_PHOTO-2026-02-09-10-35-11.jpg";

// Universal Image Component with Zoom and Description for ADA compliance
export const ImageWithZoom = ({ 
  src, 
  alt, 
  description, 
  className = "", 
  containerClassName = "",
  loading = "lazy",
  showControls = true 
}) => {
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsZoomOpen(false);
        setShowInfo(false);
      }
    };
    
    if (isZoomOpen || showInfo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomOpen, showInfo]);

  const imageDescription = description || alt || "Image";

  return (
    <>
      <div className={`relative group ${containerClassName}`}>
        <img 
          src={src} 
          alt={alt}
          className={className}
          loading={loading}
        />
        <span className="sr-only">{imageDescription}</span>
        {showControls && (
          <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsZoomOpen(true); }}
              className="p-2 bg-black/60 hover:bg-black/80 rounded-full shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={`Zoom in on ${alt}`}
              title="Zoom in"
            >
              <ZoomIn size={16} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowInfo(true); }}
              className="p-2 bg-black/60 hover:bg-black/80 rounded-full shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={`View description of ${alt}`}
              title="Image description"
            >
              <Info size={16} className="text-white" />
            </button>
          </div>
        )}
      </div>

      {isZoomOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setIsZoomOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image view"
        >
          <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsZoomOpen(false)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-white rounded-full"
              aria-label="Close zoom view"
            >
              <X size={32} />
            </button>
            <img 
              src={src} 
              alt={`${alt} - Zoomed view`} 
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
              <p className="text-white text-sm leading-relaxed">{imageDescription}</p>
            </div>
            <p className="text-center text-white/60 text-sm mt-4">Press Escape or click outside to close</p>
          </div>
        </div>
      )}

      {showInfo && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowInfo(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="image-description-title"
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 id="image-description-title" className="font-serif text-xl font-bold text-primary">Image Description</h3>
              <button
                onClick={() => setShowInfo(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close description"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="flex gap-4 items-start">
              <img 
                src={src} 
                alt={`${alt} thumbnail`} 
                className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
              />
              <div>
                <h4 className="font-semibold text-foreground mb-2">{alt}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{imageDescription}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-muted-foreground">
                <strong>Accessibility:</strong> This description helps all visitors understand the image content.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Enhanced Logo Component with Zoom and Description
export const LogoWithZoom = ({ isScrolled, size = "normal" }) => {
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  
  const sizeClasses = {
    small: "h-10 md:h-12",
    normal: "h-14 md:h-16",
    large: "h-20 md:h-28",
    hero: "h-28 md:h-40"
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsZoomOpen(false);
        setShowInfo(false);
      }
    };
    if (isZoomOpen || showInfo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomOpen, showInfo]);

  return (
    <>
      <div className="relative group">
        <div 
          className={`
            relative overflow-hidden rounded-lg transition-all duration-300
            ${isScrolled ? 'bg-white/80 backdrop-blur-sm shadow-sm' : 'bg-white/20 backdrop-blur-md'}
            p-2 hover:shadow-lg
          `}
        >
          <img 
            src={LOGO_URL} 
            alt="Palmaréca Selah Village - Luxury tropical villa rentals in Juan Dolio, Dominican Republic" 
            className={`${sizeClasses[size]} w-auto object-contain transition-transform duration-300 group-hover:scale-105`}
            loading="eager"
          />
          <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 rounded-lg">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsZoomOpen(true); }}
              className="p-2 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Zoom in on logo"
              title="Zoom in"
            >
              <ZoomIn size={16} className="text-primary" />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowInfo(true); }}
              className="p-2 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="View logo description"
              title="Image description"
            >
              <Info size={16} className="text-primary" />
            </button>
          </div>
        </div>
        <span className="sr-only">{IMAGE_DESCRIPTIONS.logo}</span>
      </div>

      {isZoomOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setIsZoomOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed logo view"
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsZoomOpen(false)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-white rounded-full"
              aria-label="Close zoom view"
            >
              <X size={32} />
            </button>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 shadow-2xl">
              <img 
                src={LOGO_URL} 
                alt="Palmaréca Selah Village - Zoomed view" 
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
              <div className="mt-4 p-4 bg-white/90 rounded-lg">
                <h3 className="font-serif text-lg font-bold text-primary mb-2">Palmaréca Selah Village</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{IMAGE_DESCRIPTIONS.logo}</p>
              </div>
            </div>
            <p className="text-center text-white/60 text-sm mt-4">Press Escape or click outside to close</p>
          </div>
        </div>
      )}

      {showInfo && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowInfo(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="logo-description-title"
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 id="logo-description-title" className="font-serif text-xl font-bold text-primary">Image Description</h3>
              <button
                onClick={() => setShowInfo(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close description"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="flex gap-4 items-start">
              <img 
                src={LOGO_URL} 
                alt="Palmaréca Selah Village logo thumbnail" 
                className="w-20 h-20 object-contain rounded-lg bg-muted/30 p-2 flex-shrink-0"
              />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Palmaréca Selah Village Logo</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{IMAGE_DESCRIPTIONS.logo}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-muted-foreground">
                <strong>Accessibility:</strong> This image description is provided to ensure all visitors can understand our brand identity.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
